import { useEffect, useMemo, useRef, useState } from 'react';

import { FormStoreInstance } from '@components/form/create-form-store';

import { flattenAllVariables } from './common';
import { parseParameterMapFieldId } from './endpoint-fields';
import type { IntegrationEndpointVariable } from './integrations-types';
import {
  buildLookupVariable,
  insertLookupBeforeField,
  removeNameFromParameterMaps,
  removeVariableByName,
} from './lookup-values';
import type { NewLookupValue } from './lookup-values';

type AddedLookup = { name: string; variable: IntegrationEndpointVariable; beforeName: string };

// Lookup add/remove are deferred: they stay pending in local state until the
// card is saved and the refetch reflects them. `currentVariables` is the saved
// set with the pending edits applied, for rendering and for the save payload.
export const usePendingLookups = (
  endpointVariables: IntegrationEndpointVariable[],
  formStore: FormStoreInstance,
) => {
  const [addedLookups, setAddedLookups] = useState<AddedLookup[]>([]);
  const [removedNames, setRemovedNames] = useState<string[]>([]);

  const savedNames = useMemo(
    () => new Set(flattenAllVariables(endpointVariables).map((item) => item.flatten)),
    [endpointVariables],
  );
  const pendingAdded = addedLookups.filter(
    (added) => !savedNames.has(added.name) || removedNames.includes(added.name),
  );
  const pendingRemoved = removedNames.filter((name) => savedNames.has(name));

  // Clear a pending change once the refetch reflects it, so it can't resurrect.
  const addedLookupsRef = useRef(addedLookups);
  addedLookupsRef.current = addedLookups;
  useEffect(() => {
    const reAdded = new Set(addedLookupsRef.current.map((added) => added.name));
    setAddedLookups((current) => {
      const next = current.filter((added) => !savedNames.has(added.name));
      return next.length === current.length ? current : next;
    });
    setRemovedNames((current) => {
      const next = current.filter((name) => savedNames.has(name) && !reAdded.has(name));
      return next.length === current.length ? current : next;
    });
  }, [savedNames]);

  const afterRemovals = pendingRemoved.reduce(
    (variables, name) => removeNameFromParameterMaps(removeVariableByName(variables, name), name),
    endpointVariables,
  );
  const currentVariables = pendingAdded.reduce(
    (variables, added) =>
      insertLookupBeforeField({
        variables,
        lookup: added.variable,
        beforeFlattenedName: added.beforeName,
      }).variables,
    afterRemovals,
  );
  const hasLookupChanges = pendingAdded.length > 0 || pendingRemoved.length > 0;

  const clearPendingLookups = () => {
    setAddedLookups([]);
    setRemovedNames([]);
  };

  const createLookup = async (
    newLookupValue: NewLookupValue,
    beforeName: string,
  ): Promise<string> => {
    const lookupVariable = buildLookupVariable(newLookupValue);
    const { lookupName } = insertLookupBeforeField({
      variables: currentVariables,
      lookup: lookupVariable,
      beforeFlattenedName: beforeName,
    });
    setAddedLookups((current) => [
      ...current,
      { name: lookupName, variable: lookupVariable, beforeName },
    ]);
    return lookupName;
  };

  const clearParameterMapValuesFor = (name: string) => {
    for (const [fieldId, value] of Object.entries(formStore.getState().values)) {
      if (value === name && parseParameterMapFieldId(fieldId)) {
        formStore.actions.setValue(fieldId, null);
      }
    }
  };

  const removeLookup = (name: string) => {
    clearParameterMapValuesFor(name);
    if (pendingAdded.some((added) => added.name === name)) {
      setAddedLookups((current) => current.filter((added) => added.name !== name));
      return;
    }
    setRemovedNames((current) => [...current, name]);
  };

  return { currentVariables, hasLookupChanges, clearPendingLookups, createLookup, removeLookup };
};
